import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowRight, MessageCircle, ShieldCheck, Clock, Globe, BedDouble,
  Stethoscope, CheckCircle2, Loader2, Send, MapPin, AlertTriangle,
  Activity, Droplets, Thermometer, Sun, HeartPulse, Scissors, Pill,
  HelpCircle, ChevronDown, FileCheck2, Compass,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { submitContactForm } from '../lib/api';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { track } from '../lib/analytics';
import {
  getUrgentLocationBySlug,
  ALL_URGENT_LOCATIONS,
  URGENT_LOCATION_SYMPTOMS,
} from '../data/urgentCareLocations';
import { UrgentConditionsServices } from '../components/UrgentCareSections';

/**
 * Shared template for "confirm-on-contact" urgent-care location pages:
 *   /urgent-medical-care-hue, -lang-co, -dong-hoi, -phong-nha
 *
 * VietClinics runs a 24/7 coordination line with English-speaking support,
 * but doctor availability, the service area and the estimated arrival time are
 * CONFIRMED FOR THE PATIENT'S EXACT ADDRESS when they contact us. This template
 * intentionally omits any "arrives within 60 minutes" or "doctor available 24/7"
 * guarantee and any priced-service catalogue.
 */

// Safe, coordination-only trust badges (no response-time promise).
const TRUST_BADGES = [
  { icon: Clock,       label: '24/7 Coordination Line' },
  { icon: Globe,       label: 'English-Speaking Support' },
  { icon: Stethoscope, label: 'Licensed Local Doctors' },
  { icon: ShieldCheck, label: 'Availability Confirmed on Contact' },
  { icon: BedDouble,   label: 'Hotel, Apartment & Airbnb Visits' },
  { icon: FileCheck2,  label: 'Insurance Documents on Request' },
];

const SYMPTOM_ICONS = [Pill, Activity, Droplets, Thermometer, Sun, HeartPulse, Scissors];

const COUNTRIES = [
  'Australia', 'Canada', 'France', 'Germany', 'India', 'Indonesia',
  'Japan', 'Malaysia', 'Netherlands', 'New Zealand', 'Philippines',
  'Singapore', 'South Korea', 'Thailand', 'United Kingdom',
  'United States', 'Vietnam', 'Other',
];

const inputCls =
  'w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 '
  + 'placeholder:text-slate-400 focus:outline-none focus:border-[#1FA7A1] focus:ring-2 '
  + 'focus:ring-[#1FA7A1]/15 transition-colors';

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
    {children}
  </label>
);

/* ───────────────────────────────────────────────────────────────────────── */

const UrgentMedicalCareLocationPage = ({ slug }) => {
  const loc = getUrgentLocationBySlug(slug);
  const { contactInfo } = useContactInfo();

  const [form, setForm] = useState({
    fullName: '', whatsapp: '', email: '', country: '',
    stay: '', date: '', symptoms: '',
  });
  const [status, setStatus] = useState({ loading: false, sent: false, error: '' });

  const onChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  // Guard: unknown slug → back to the hub.
  if (!loc) return <Navigate to="/urgent-medical-care" replace />;

  const whatsappHref = getWhatsAppHref(
    contactInfo,
    `Hi VietClinics! I need urgent medical support in ${loc.cityDisplay}. Can you help?`,
  );

  const siblings = ALL_URGENT_LOCATIONS.filter((l) => l.slug !== loc.slug);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.whatsapp.trim()) {
      setStatus({ loading: false, sent: false, error: 'Please complete your name and WhatsApp number.' });
      return;
    }
    setStatus({ loading: true, sent: false, error: '' });

    const message = [
      `URGENT — ${loc.cityDisplay} Medical Support Request from ${form.fullName}.`,
      `WhatsApp: ${form.whatsapp}`,
      form.email && `Email: ${form.email}`,
      form.country && `Country: ${form.country}`,
      form.stay && `Where staying (exact address): ${form.stay}`,
      form.date && `Preferred date/time: ${form.date}`,
      form.symptoms && `Symptoms / message: ${form.symptoms}`,
    ].filter(Boolean).join('\n');

    try {
      await submitContactForm({
        name: form.fullName,
        email: form.email || `wa-${form.whatsapp.replace(/[^0-9]/g, '')}@vietclinics-request.com`,
        phone: form.whatsapp,
        subject: `Urgent ${loc.cityName} Support — coordination request`,
        message,
      });
      track('urgent_care_location_submit', { location: loc.slug, status: 'delivered' });
    } catch (err) {
      // Form never blocks the conversion path — log + fall back to success.
      // eslint-disable-next-line no-console
      console.warn('[UrgentLocation] submission failed, falling back', err);
      track('urgent_care_location_submit', { location: loc.slug, status: 'fallback' });
    }
    setStatus({ loading: false, sent: true, error: '' });
  };

  /* ── SEO JSON-LD ──────────────────────────────────────────────────────── */
  const canonicalUrl = `https://vietclinics.com${loc.href}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'MedicalBusiness',
        '@id': `${canonicalUrl}#business`,
        name: `VietClinics — Urgent Medical Support in ${loc.cityName}`,
        description:
          `VietClinics coordinates 24/7 urgent, non-emergency medical support with English-speaking `
          + `help for visitors, expats and families in ${loc.cityName}, Vietnam. Doctor availability, `
          + `service area and estimated arrival time are confirmed for the patient's exact address.`,
        url: canonicalUrl,
        areaServed: [{ '@type': 'City', name: loc.cityName, addressCountry: 'VN' }],
        medicalSpecialty: ['GeneralPractice'],
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: loc.h1,
        inLanguage: 'en',
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        mainEntity: loc.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vietclinics.com' },
          { '@type': 'ListItem', position: 2, name: 'Urgent Medical Care', item: 'https://vietclinics.com/urgent-medical-care' },
          { '@type': 'ListItem', position: 3, name: loc.cityName, item: canonicalUrl },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`urgent-location-${loc.slug}`}>
      <Helmet>
        <title>{loc.metaTitle}</title>
        <meta name="description" content={loc.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={loc.metaTitle} />
        <meta property="og:description" content={loc.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={loc.heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="urgent-location-hero">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, #F0FAF8 0%, #FAFCFB 65%, #FFFFFF 100%)' }}
        />
        <div
          aria-hidden
          className="absolute top-[-10%] right-[-8%] rounded-full pointer-events-none"
          style={{
            width: 620, height: 620,
            background: 'radial-gradient(circle, rgba(31,167,161,0.18) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div className="relative z-10 container mx-auto px-4 max-w-7xl pt-12 lg:pt-20 pb-16 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div
                className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest"
                style={{ background: '#DCEFEC', color: '#1A8F8A' }}
                data-testid="urgent-location-eyebrow"
              >
                <MapPin className="h-3.5 w-3.5" />
                {loc.heroEyebrow}
              </div>

              <h1
                className="font-extrabold tracking-tight text-slate-900 leading-[1.06]"
                style={{ fontSize: 'clamp(2.1rem, 4.2vw, 3.4rem)' }}
                data-testid="urgent-location-title"
              >
                {loc.h1}
              </h1>

              <p className="text-slate-600 text-base lg:text-lg leading-relaxed max-w-xl" data-testid="urgent-location-subtitle">
                {loc.heroSubtitle}
              </p>

              <div className="flex flex-wrap gap-3 mt-1">
                <a
                  href="#request-care"
                  className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-7 py-4 transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                    boxShadow: '0 12px 24px rgba(31,167,161,0.25)',
                  }}
                  data-testid="urgent-location-cta-request"
                >
                  <Stethoscope className="h-4 w-4" />
                  Request Urgent Support
                </a>
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-2xl px-7 py-4 border-2 border-[#1FA7A1] hover:bg-[#1FA7A1]/5 transition-all"
                    data-testid="urgent-location-cta-whatsapp"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chat on WhatsApp
                  </a>
                )}
              </div>

              <p className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                <ShieldCheck className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                Coordinated by VietClinics — an independent, non-emergency coordination service.
              </p>

              {/* ── Safety / emergency-redirect strip ──────────────────────── */}
              <div
                className="mt-4 rounded-2xl border border-[#FEC84B]/60 bg-[#FFFAEB] px-4 py-3 flex gap-3 items-start"
                role="note"
                data-testid="urgent-location-safety-note"
              >
                <AlertTriangle className="h-5 w-5 text-[#B54708] flex-shrink-0 mt-0.5" />
                <p className="text-[12.5px] text-[#7A2E0E] leading-snug">
                  <strong className="block text-[13px] mb-0.5">For life-threatening emergencies, contact local services immediately.</strong>
                  In Vietnam dial <strong>115</strong> for an ambulance or go to the nearest hospital
                  emergency department. VietClinics coordinates urgent, non-emergency care — not emergency response.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div
                className="relative rounded-[32px] overflow-hidden aspect-[4/5]"
                style={{ boxShadow: '0 30px 80px rgba(15,42,85,0.18)' }}
              >
                <img
                  src={loc.heroImage}
                  alt={`${loc.cityDisplay}, Vietnam — where VietClinics coordinates urgent medical support for visitors`}
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => { e.target.src = loc.heroFallback; }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(31,167,161,0.10) 0%, transparent 40%, rgba(31,167,161,0.18) 100%)',
                  }}
                />
              </div>

              <div
                className="hidden sm:block absolute -left-6 bottom-8 bg-white rounded-[24px] p-5 max-w-[270px]"
                style={{ boxShadow: '0 20px 50px rgba(15,42,85,0.16)' }}
                data-testid="urgent-location-availability-card"
              >
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
                  Availability
                </p>
                <p className="text-xl font-extrabold text-slate-900 leading-tight">
                  Confirmed on contact
                </p>
                <p className="text-xs text-slate-500 mt-1 leading-snug">
                  We confirm doctor availability and the estimated arrival time for your exact address.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100" data-testid="urgent-location-trust-strip">
        <div className="container mx-auto px-4 max-w-7xl py-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            {TRUST_BADGES.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className="flex items-center gap-3"
                data-testid={`urgent-location-trust-${i + 1}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: '#DCEFEC' }}
                >
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTRO / LOCAL CONTEXT ────────────────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-white" data-testid="urgent-location-intro">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            {loc.cityDisplay}
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.12] mb-6">
            {loc.introHeading}
          </h2>
          <div className="space-y-5">
            {loc.introParagraphs.map((p, i) => (
              <p key={i} className="text-slate-600 text-base lg:text-lg leading-relaxed" data-testid={`urgent-location-intro-p-${i + 1}`}>
                {p}
              </p>
            ))}
          </div>

          <div
            className="mt-8 rounded-2xl border border-[#1FA7A1]/25 bg-[#F0FAF8] px-5 py-4 flex gap-3 items-start"
            data-testid="urgent-location-availability-note"
          >
            <ShieldCheck className="h-5 w-5 text-[#1A8F8A] flex-shrink-0 mt-0.5" />
            <p className="text-[13px] text-slate-700 leading-snug">{loc.availabilityNote}</p>
          </div>
        </div>
      </section>

      {/* ── WHERE VISITORS STAY (CONTEXT, NOT CONFIRMED COVERAGE) ─────────── */}
      <section className="py-20 lg:py-24 bg-[#F8FAFC]" data-testid="urgent-location-areas">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              Local context
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              {loc.areasHeading}
            </h2>
            <p className="text-slate-500 mt-4 text-sm lg:text-base leading-relaxed">
              {loc.areasNote}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {loc.areas.map((a, i) => (
              <article
                key={a.name}
                className="bg-white rounded-[24px] border border-slate-100 p-7"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
                data-testid={`urgent-location-area-${i + 1}`}
              >
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 mb-4" style={{ background: '#DCEFEC' }}>
                  <Compass className="h-5 w-5 text-[#1A8F8A]" />
                </div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-2 leading-snug">{a.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{a.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── SYMPTOMS & CARE ──────────────────────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-white" data-testid="urgent-location-symptoms">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              Symptoms &amp; care
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              Common reasons visitors request help
            </h2>
            <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
              Urgent medical support for visitors usually starts with a common, non-emergency problem.
              VietClinics can coordinate help for issues such as these:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {URGENT_LOCATION_SYMPTOMS.map((label, i) => {
              const Icon = SYMPTOM_ICONS[i % SYMPTOM_ICONS.length];
              return (
                <div
                  key={label}
                  className="flex items-center gap-3 bg-[#F8FAFC] rounded-2xl border border-slate-100 px-4 py-4"
                  data-testid={`urgent-location-symptom-${i + 1}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
                    <Icon className="h-4 w-4 text-[#1A8F8A]" />
                  </div>
                  <span className="text-sm font-semibold text-slate-800 leading-snug">{label}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-8 rounded-[24px] border border-slate-100 bg-[#F0FAF8] p-7 sm:p-8" data-testid="urgent-location-care-note">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white">
                <Stethoscope className="h-5 w-5 text-[#1A8F8A]" />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-2">The treating doctor decides what care is appropriate</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  If a visit is arranged, a licensed local doctor assesses the patient and decides what
                  care is appropriate — this may include advice, a prescription, treatment at your
                  address, or a referral to a clinic or hospital where needed. Care options always
                  depend on the doctor&rsquo;s assessment; nothing is promised in advance.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-[#FEC84B]/60 bg-[#FFFAEB] px-5 py-4 flex gap-3 items-start" role="note" data-testid="urgent-location-symptoms-emergency">
            <AlertTriangle className="h-5 w-5 text-[#B54708] flex-shrink-0 mt-0.5" />
            <p className="text-[13px] text-[#7A2E0E] leading-snug">
              <strong>For life-threatening emergencies, call 115 or go to the nearest hospital emergency department.</strong>{' '}
              VietClinics coordinates urgent, non-emergency care — not emergency response.
            </p>
          </div>
        </div>
      </section>

      {/* ── CONDITIONS & SERVICES (additive SEO) ─────────────────────────── */}
      <UrgentConditionsServices testId={`urgent-location-conditions-${loc.slug}`} />

      {/* ── HOW TO GET HELP ──────────────────────────────────────────────── */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="urgent-location-how-it-works">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="max-w-3xl mb-12 text-center mx-auto">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              How to get help
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              Three simple steps to request support in {loc.cityName}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {loc.howItWorks.map((step, i) => (
              <div
                key={step.title}
                className="bg-white rounded-[20px] border border-slate-100 p-6"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`urgent-location-step-${i + 1}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-white mb-4"
                  style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' }}
                >
                  {i + 1}
                </div>
                <h3 className="font-extrabold text-slate-900 text-base leading-snug mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REQUEST FORM + WHATSAPP SUPPORT CARD ─────────────────────────── */}
      <section id="request-care" className="py-20 bg-white" data-testid="urgent-location-request-section">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Form */}
            <div
              className="lg:col-span-2 bg-white rounded-[28px] border border-slate-100 p-6 sm:p-10"
              style={{ boxShadow: '0 20px 60px rgba(15,42,85,0.08)' }}
            >
              <div className="mb-6">
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
                  Care Request
                </p>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  Request urgent support in {loc.cityName}
                </h3>
                <p className="text-slate-500 mt-2 text-sm sm:text-base leading-relaxed">
                  Share a few details and your exact address. Our English-speaking team confirms whether
                  a doctor visit can be arranged and the estimated arrival time, 24/7.
                </p>
              </div>

              {status.sent ? (
                <div className="text-center max-w-md mx-auto py-8" data-testid="urgent-location-form-success">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                  </div>
                  <h4 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                    Request received
                  </h4>
                  <p className="text-slate-500 mt-2 leading-relaxed">
                    Thanks {form.fullName.split(' ')[0]}. Our team will contact you on WhatsApp very
                    shortly to confirm availability and the estimated arrival time for your address.
                  </p>
                  {whatsappHref && (
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold rounded-full px-6 py-3 transition-colors"
                      data-testid="urgent-location-form-whatsapp-link"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Continue on WhatsApp
                    </a>
                  )}
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  data-testid="urgent-location-form"
                >
                  <Field label="Full Name *">
                    <input
                      type="text"
                      required
                      value={form.fullName}
                      onChange={onChange('fullName')}
                      placeholder="Jane Smith"
                      className={inputCls}
                      data-testid="urgent-location-input-name"
                    />
                  </Field>
                  <Field label="WhatsApp / Phone *">
                    <input
                      type="tel"
                      required
                      value={form.whatsapp}
                      onChange={onChange('whatsapp')}
                      placeholder="+1 ..."
                      className={inputCls}
                      data-testid="urgent-location-input-whatsapp"
                    />
                  </Field>

                  <Field label="Email">
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChange('email')}
                      placeholder="you@example.com"
                      className={inputCls}
                      data-testid="urgent-location-input-email"
                    />
                  </Field>
                  <Field label="Country">
                    <select
                      value={form.country}
                      onChange={onChange('country')}
                      className={inputCls}
                      data-testid="urgent-location-input-country"
                    >
                      <option value="">Select your country</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>

                  <Field label={`Where are you staying in ${loc.cityName}?`} className="sm:col-span-2">
                    <input
                      type="text"
                      value={form.stay}
                      onChange={onChange('stay')}
                      placeholder="Hotel / guesthouse / homestay name & exact address"
                      className={inputCls}
                      data-testid="urgent-location-input-stay"
                    />
                  </Field>

                  <Field label="Preferred Date / Time">
                    <input
                      type="datetime-local"
                      value={form.date}
                      onChange={onChange('date')}
                      className={inputCls}
                      data-testid="urgent-location-input-date"
                    />
                  </Field>

                  <Field label="Symptoms / Message" className="sm:col-span-1">
                    <input
                      type="text"
                      value={form.symptoms}
                      onChange={onChange('symptoms')}
                      placeholder="Briefly describe symptoms & any allergies"
                      className={inputCls}
                      data-testid="urgent-location-input-symptoms"
                    />
                  </Field>

                  {status.error && (
                    <p className="sm:col-span-2 text-sm text-red-500 font-medium">{status.error}</p>
                  )}

                  <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
                    <p className="flex items-center gap-2 text-xs text-slate-500">
                      <ShieldCheck className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                      Coordinated by VietClinics — your details stay private.
                    </p>
                    <button
                      type="submit"
                      disabled={status.loading}
                      className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#178f8a] disabled:opacity-60 text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                      data-testid="urgent-location-form-submit"
                    >
                      {status.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Submit Request
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* WhatsApp side card */}
            <aside className="lg:col-span-1" data-testid="urgent-location-whatsapp-card">
              <div
                className="rounded-[28px] p-8 text-white relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
              >
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.10] pointer-events-none"
                  style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%)' }}
                />
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center mb-4 backdrop-blur-sm">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-extrabold tracking-tight leading-tight mb-2">
                    Need Help Now?
                  </h3>
                  <p className="text-white/85 text-sm leading-relaxed mb-6">
                    Chat with our English-speaking team on WhatsApp. We confirm whether a doctor visit
                    can be arranged for your exact address, and the estimated arrival time, 24/7.
                  </p>
                  {whatsappHref ? (
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-full px-6 py-3 hover:-translate-y-0.5 transition-all"
                      data-testid="urgent-location-whatsapp-card-cta"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chat on WhatsApp
                    </a>
                  ) : (
                    <a
                      href="#request-care"
                      className="inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-bold rounded-full px-6 py-3 hover:-translate-y-0.5 transition-all"
                      data-testid="urgent-location-whatsapp-card-cta-fallback"
                    >
                      <Stethoscope className="h-4 w-4" />
                      Use the request form
                    </a>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-[24px] border border-slate-100 p-6 mt-5"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
              >
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
                  Why VietClinics
                </p>
                <ul className="space-y-2.5 mt-3">
                  {[
                    '24/7 English-speaking coordination',
                    'Licensed local doctors',
                    'Availability confirmed for your address',
                    'Hotel, apartment & Airbnb visits',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-24 bg-[#F8FAFC]" data-testid="urgent-location-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="max-w-3xl mb-10">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
              FAQs
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
              {loc.cityName} urgent medical support — your questions
            </h2>
          </div>

          <div className="space-y-3">
            {loc.faq.map((f, i) => (
              <details
                key={f.q}
                className="group bg-white rounded-2xl border border-slate-100 px-5 sm:px-6 py-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`urgent-location-faq-${i + 1}`}
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-bold text-slate-900 text-base leading-snug">
                  <span className="flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                    {f.q}
                  </span>
                  <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0 transition-transform duration-300 group-open:rotate-180" />
                </summary>
                <p className="text-sm text-slate-600 leading-relaxed mt-3 pl-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── RELATED LOCATIONS + HUB ──────────────────────────────────────── */}
      <section className="py-20 bg-white border-t border-slate-100" data-testid="urgent-location-related">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
                More locations
              </p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
                Urgent care coordination across Vietnam
              </h2>
            </div>
            <Link
              to="/urgent-medical-care"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#1FA7A1] hover:gap-3 transition-all"
              data-testid="urgent-location-hub-link"
            >
              View the Urgent Care hub
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={s.href}
                className="group bg-[#F8FAFC] rounded-[20px] border border-slate-100 p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                data-testid={`urgent-location-related-${s.slug}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#DCEFEC' }}>
                  <MapPin className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <h3 className="font-extrabold text-slate-900 text-base leading-snug mb-1">{s.cityDisplay}</h3>
                <p className="text-xs text-slate-500 mb-4">{s.region}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1] group-hover:gap-2.5 transition-all">
                  View support
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
            <Link
              to="/urgent-medical-care-hoi-an"
              className="group bg-[#F8FAFC] rounded-[20px] border border-slate-100 p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
              data-testid="urgent-location-related-hoi-an"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#DCEFEC' }}>
                <MapPin className="h-4 w-4 text-[#1A8F8A]" />
              </div>
              <h3 className="font-extrabold text-slate-900 text-base leading-snug mb-1">Da Nang &amp; Hoi An</h3>
              <p className="text-xs text-slate-500 mb-4">Central Coast</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1] group-hover:gap-2.5 transition-all">
                View support
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UrgentMedicalCareLocationPage;
